import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import Modal from "./Modal";
import { FileText, Wallet, UserPlus, UserCheck, Info, Edit3, ChevronDown, Search, Trash2 } from "lucide-react";
import "../styles/KpiFormModal.css";

export default function AddMasterModal({ initialTab, onClose, onSuccess }) {
  const API = process.env.REACT_APP_API_BASE_URL || "";
  
  const [modalTab, setModalTab] = useState(initialTab === "tabungan" ? "tabungan" : "kpi");
  const [userMode, setUserMode] = useState("lama"); 
  
  const [loading, setLoading] = useState(false);
  // 🔥 State infoModal diperbarui untuk mendukung kustomisasi tombol
  const [infoModal, setInfoModal] = useState({ 
    isOpen: false, 
    title: "", 
    message: "", 
    type: "info",
    confirmOnly: false,
    confirmLabel: "Oke",
    onConfirm: null
  });
  
  const [masterKpi, setMasterKpi] = useState([]);
  const [masterTabungan, setMasterTabungan] = useState([]);

  const [form, setForm] = useState({
    nama: "", divisi: "", unit: "",
    area_kinerja: "", parameter: "", sumber: "", indikator_kpi: "",
    kerja_tabungan_gaji: "", hasil_berwujud: "", bukti_nilai_teks: "", konversi_uang: "",
    target: "100", bobot: "",
  });

  const [selectedCloneUnit, setSelectedCloneUnit] = useState("");
  const [clonedDataPreview, setClonedDataPreview] = useState([]);

  const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
  const [nameSearch, setNameSearch] = useState("");
  
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [unitSearch, setUnitSearch] = useState("");

  const nameDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nameDropdownRef.current && !nameDropdownRef.current.contains(event.target)) setIsNameDropdownOpen(false);
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) setIsUnitDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchAllMaster = async () => {
      try {
        const [resKpi, resTab] = await Promise.all([
          axios.get(`${API}/api/indikator-data`),
          axios.get(`${API}/api/tabungan-data`)
        ]);
        if(resKpi.data.result === 'success') setMasterKpi(resKpi.data.message);
        if(resTab.data.result === 'success') setMasterTabungan(resTab.data.message);
      } catch(err) { console.error(err); }
    };
    fetchAllMaster();
  }, [API]);

  const activeMaster = useMemo(() => modalTab === "kpi" ? masterKpi : masterTabungan, [modalTab, masterKpi, masterTabungan]);
  
  const uniqueNames = useMemo(() => [...new Set(activeMaster.map(d => d.nama || d.NAMA).filter(Boolean))].sort(), [activeMaster]);
  const uniqueUnits = useMemo(() => [...new Set(activeMaster.map(d => d.unit || d.UNIT).filter(Boolean))].sort(), [activeMaster]);

  const filteredNames = useMemo(() => {
    if(!nameSearch) return uniqueNames;
    const lower = nameSearch.toLowerCase();
    return uniqueNames.filter(n => n.toLowerCase().includes(lower));
  }, [nameSearch, uniqueNames]);

  const filteredUnits = useMemo(() => {
    if(!unitSearch) return uniqueUnits;
    const lower = unitSearch.toLowerCase();
    return uniqueUnits.filter(u => u.toLowerCase().includes(lower));
  }, [unitSearch, uniqueUnits]);

  const parseBobotToPercent = (val) => {
    if (!val && val !== 0) return "0";
    let strVal = String(val).replace(",", ".").trim();
    if (strVal.includes("%")) return parseFloat(strVal.replace("%", "")).toString();
    let num = parseFloat(strVal);
    if (!isNaN(num) && num > 0 && num <= 1) return Math.round(num * 100).toString();
    return num.toString();
  };

  const handleNameSelectLama = useCallback((selectedName) => {
    const person = activeMaster.find(d => (d.nama || d.NAMA) === selectedName);
    setForm(prev => ({
      ...prev, 
      nama: selectedName, 
      divisi: person?.divisi || "", 
      unit: person?.unit || "" 
    }));
    setIsNameDropdownOpen(false);
    setNameSearch("");
  }, [activeMaster]);

  const handleCloneUnitChange = (unit) => {
    setSelectedCloneUnit(unit);
    setIsUnitDropdownOpen(false);
    setUnitSearch("");

    if(!unit) {
      setClonedDataPreview([]);
      return;
    }

    const representative = activeMaster.find(d => (d.unit || d.UNIT) === unit);
    if(!representative) return;

    const repName = representative.nama || representative.NAMA;
    const templateItems = activeMaster.filter(d => (d.nama || d.NAMA) === repName);
    
    const newUserName = form.nama.trim().charAt(0).toUpperCase() + form.nama.trim().slice(1);
    const escapedRepName = repName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regexExactName = new RegExp(escapedRepName, "gi");
    const regexKurungAkhir = /\(([^)]+)\)\s*$/;

    const preparedData = templateItems.map(item => {
      let updatedText = modalTab === "kpi" ? item.indikator_kpi : (item.kerja_tabungan_gaji || item["Kerja Tabungan Gaji"]);
      if (updatedText) {
        updatedText = updatedText.replace(regexExactName, newUserName);
        if (regexKurungAkhir.test(updatedText)) {
          updatedText = updatedText.replace(regexKurungAkhir, `(${newUserName})`);
        }
      }
      return {
        ...item,
        nama: newUserName,
        divisi: representative.divisi,
        unit: unit,
        [modalTab === "kpi" ? "indikator_kpi" : "kerja_tabungan_gaji"]: updatedText,
        target: item.target || "100",
        bobot: parseBobotToPercent(item.bobot || item.Bobot)
      };
    });

    setForm(prev => ({...prev, divisi: representative.divisi, unit: unit}));
    setClonedDataPreview(preparedData);
  };

  const handleEditCloneItem = useCallback((index, field, value) => {
    setClonedDataPreview(prev => {
      const newArr = [...prev];
      newArr[index] = { ...newArr[index], [field]: value };
      return newArr;
    });
  }, []);

  const handleRemoveCloneItem = useCallback((index) => {
    setClonedDataPreview(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // 🔥 LOGIKA SIMPAN & MUNCULKAN MODAL LOADING 🔥
  const handleSubmit = async () => {
    if (!form.nama) {
      setInfoModal({ isOpen: true, title: "Perhatian", message: "Nama Karyawan wajib diisi!", type: "warning", confirmOnly: true, confirmLabel: "Mengerti" });
      return;
    }

    let payloadItems = [];

    if (userMode === "baru" && selectedCloneUnit) {
      if(clonedDataPreview.length === 0) {
        setInfoModal({ isOpen: true, title: "Perhatian", message: "Tidak ada data untuk disimpan.", type: "warning", confirmOnly: true, confirmLabel: "Mengerti" }); return;
      }
      payloadItems = clonedDataPreview; 
    } else {
      if (modalTab === "kpi" && !form.indikator_kpi) {
        setInfoModal({ isOpen: true, title: "Perhatian", message: "Indikator KPI wajib diisi!", type: "warning", confirmOnly: true, confirmLabel: "Mengerti" }); return;
      }
      if (modalTab === "tabungan" && !form.kerja_tabungan_gaji) {
        setInfoModal({ isOpen: true, title: "Perhatian", message: "Kerja Tabungan Gaji wajib diisi!", type: "warning", confirmOnly: true, confirmLabel: "Mengerti" }); return;
      }
      payloadItems = [form];
    }

    setLoading(true);
    try {
      const payload = { dataType: modalTab, items: payloadItems };
      const res = await axios.post(`${API}/api/add-master-data`, payload);
      
      if (res.data.result === "success") {
        // Matikan loading dulu, lalu tampilkan modal sukses 1 tombol
        setLoading(false);
        setInfoModal({
          isOpen: true, 
          title: "Berhasil", 
          message: "Data berhasil disimpan!", 
          type: "success",
          confirmOnly: true, // Cuma ada 1 tombol
          confirmLabel: "Selesai", // Label tombolnya "Selesai"
          onConfirm: () => { 
            setInfoModal(prev => ({ ...prev, isOpen: false })); 
            onSuccess(); 
          }
        });
      } else {
        setLoading(false);
        setInfoModal({ isOpen: true, title: "Gagal", message: "Gagal menyimpan data.", type: "error", confirmOnly: true, confirmLabel: "Tutup" });
      }
    } catch (err) {
      setLoading(false);
      setInfoModal({ isOpen: true, title: "Error", message: "Terjadi kesalahan server.", type: "error", confirmOnly: true, confirmLabel: "Tutup" });
    }
  };

  const renderClonedItems = useMemo(() => {
    return clonedDataPreview.map((item, index) => (
      <div key={index} className="clone-clean-card">
        <div className="clone-card-top">
          <label className="clone-label-clean">
            {modalTab === "kpi" ? "INDIKATOR KPI" : "KERJA TABUNGAN GAJI"}
          </label>
          <button onClick={() => handleRemoveCloneItem(index)} className="clone-btn-delete" title="Hapus Indikator Ini">
            <Trash2 size={16} />
          </button>
        </div>
        <textarea 
          value={modalTab === "kpi" ? item.indikator_kpi : item.kerja_tabungan_gaji} 
          onChange={(e) => handleEditCloneItem(index, modalTab === "kpi" ? 'indikator_kpi' : 'kerja_tabungan_gaji', e.target.value)} 
          className="clone-input-minimal textarea-minimal" 
          rows={2}
        />
        <div className="clone-row-split">
          <div className="clone-field">
            <label className="clone-label-clean">TARGET</label>
            <input 
              value={item.target} 
              onChange={(e) => handleEditCloneItem(index, 'target', e.target.value)} 
              className="clone-input-minimal" 
            />
          </div>
          <div className="clone-field">
            <label className="clone-label-clean">BOBOT (%)</label>
            <input 
              value={item.bobot} 
              type="number" 
              onChange={(e) => handleEditCloneItem(index, 'bobot', e.target.value)} 
              className="clone-input-minimal" 
            />
          </div>
        </div>
      </div>
    ));
  }, [clonedDataPreview, modalTab, handleEditCloneItem, handleRemoveCloneItem]);

  return (
    <>
      <div className="rsia-modal-backdrop">
        <div className="rsia-modal" style={{ maxWidth: "750px" }}>
          
          <div className="rsia-modal-header">
            <h5>Kelola Master Indikator</h5>
            <button className="rsia-modal-close" onClick={onClose} disabled={loading}>&times;</button>
          </div>

          <div className="rsia-modal-body" style={{ padding: "24px" }}>
            
            {/* SEGMENTED CONTROL */}
            <div className="master-tab-container">
              <button 
                className={`master-tab-btn kpi ${modalTab === "kpi" ? "active" : ""}`}
                onClick={() => { setModalTab("kpi"); setClonedDataPreview([]); setSelectedCloneUnit(""); }}
              >
                <FileText size={18} /> Data KPI
              </button>
              <button 
                className={`master-tab-btn tabungan ${modalTab === "tabungan" ? "active" : ""}`}
                onClick={() => { setModalTab("tabungan"); setClonedDataPreview([]); setSelectedCloneUnit(""); }}
              >
                <Wallet size={18} /> Data Tabungan
              </button>
            </div>

            {/* MODE SELECTION */}
            <div className="master-mode-container">
              <label className={`master-mode-card lama ${userMode === "lama" ? "active" : ""}`}>
                <input type="radio" name="userMode" value="lama" checked={userMode === "lama"} onChange={() => { setUserMode("lama"); setClonedDataPreview([]); setSelectedCloneUnit(""); setForm(f => ({...f, nama:""})); }} />
                <div>
                  <div className="mode-title"><UserCheck size={18} color="#0284c7"/> Karyawan Lama</div>
                  <div className="mode-desc">Tambah 1 indikator baru ke daftar milik karyawan yang sudah ada.</div>
                </div>
              </label>

              <label className={`master-mode-card baru ${userMode === "baru" ? "active" : ""}`}>
                <input type="radio" name="userMode" value="baru" checked={userMode === "baru"} onChange={() => { setUserMode("baru"); setForm(f => ({...f, nama:"", divisi:"", unit:""})); }} />
                <div>
                  <div className="mode-title"><UserPlus size={18} color="#d97706"/> Karyawan Baru</div>
                  <div className="mode-desc">Salin secara massal seluruh indikator dari template unit/jabatan.</div>
                </div>
              </label>
            </div>

            {/* ================= KARYAWAN LAMA ================= */}
            {userMode === "lama" && (
              <div className="master-form-section">
                <h4><Info size={18} color="#0284c7"/> Pilih Identitas Karyawan</h4>
                
                <div className="rsia-form-group">
                  <label>Nama Karyawan</label>
                  <div className="premium-dropdown-wrapper" ref={nameDropdownRef}>
                    <div className={`premium-dropdown-trigger ${isNameDropdownOpen ? "open" : ""}`} onClick={() => setIsNameDropdownOpen(!isNameDropdownOpen)}>
                      <span>{form.nama || "-- Pilih Nama Karyawan --"}</span>
                      <ChevronDown className="icon" size={20} />
                    </div>

                    {isNameDropdownOpen && (
                      <div className="premium-dropdown-menu">
                        <div className="premium-search-box">
                          <Search size={16} color="#94a3b8" />
                          <input type="text" placeholder="Ketik untuk mencari nama..." value={nameSearch} onChange={(e) => setNameSearch(e.target.value)} autoFocus />
                        </div>
                        <ul className="premium-options-list">
                          {filteredNames.length > 0 ? (
                            filteredNames.map((n, i) => (
                              <li key={i} className="premium-option-item" onClick={() => handleNameSelectLama(n)}>{n}</li>
                            ))
                          ) : (
                            <li className="premium-option-item" style={{color: "#94a3b8", textAlign:"center"}}>Nama tidak ditemukan</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="rsia-form-row">
                  <div className="rsia-form-group"><label>Divisi</label><input name="divisi" value={form.divisi} readOnly className="rsia-input-locked" /></div>
                  <div className="rsia-form-group"><label>Unit / Jabatan</label><input name="unit" value={form.unit} readOnly className="rsia-input-locked" /></div>
                </div>
              </div>
            )}

            {/* ================= KARYAWAN BARU ================= */}
            {userMode === "baru" && (
              <div className="premium-clone-wrapper" style={{ paddingBottom: clonedDataPreview.length > 0 ? "0" : "24px" }}>
                <div className="clone-step">
                  <div className="step-header">
                    <span className="step-number">1</span>
                    <label style={{color: "#b45309"}}>Ketik Nama Karyawan Baru Lengkap</label>
                  </div>
                  <input name="nama" value={form.nama} onChange={handleChange} placeholder="Contoh: dr. Andi Setiawan..." disabled={loading} className="clone-input-clean" style={{borderColor: "#fcd34d"}}/>
                </div>
                
                <div className="clone-step">
                  <div className="step-header">
                    <span className="step-number">2</span>
                    <label style={{color: "#b45309"}}>Salin Template Dari Unit/Jabatan</label>
                  </div>
                  <div className="premium-dropdown-wrapper" ref={unitDropdownRef}>
                    <div 
                      className={`clone-input-clean premium-dropdown-trigger ${isUnitDropdownOpen ? "open" : ""}`} 
                      style={{ border: "2px solid #fcd34d", fontWeight: "bold", color: "#92400e" }}
                      onClick={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                    >
                      <span>{selectedCloneUnit || "-- Pilih Unit Untuk di Duplikasi --"}</span>
                      <ChevronDown className="icon" size={20} color="#92400e" />
                    </div>

                    {isUnitDropdownOpen && (
                      <div className="premium-dropdown-menu">
                        <div className="premium-search-box">
                          <Search size={16} color="#94a3b8" />
                          <input type="text" placeholder="Cari nama unit/jabatan..." value={unitSearch} onChange={(e) => setUnitSearch(e.target.value)} autoFocus />
                        </div>
                        <ul className="premium-options-list">
                          {filteredUnits.length > 0 ? (
                            filteredUnits.map((u, i) => (
                              <li key={i} className="premium-option-item" onClick={() => handleCloneUnitChange(u)}>{u}</li>
                            ))
                          ) : (
                            <li className="premium-option-item" style={{color: "#94a3b8", textAlign:"center"}}>Unit tidak ditemukan</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {clonedDataPreview.length > 0 && (
                  <div className="clone-result-container">
                    <div className="clone-result-header">
                      <div className="result-title">
                        <FileText size={18} color="#0f172a" />
                        Data ({clonedDataPreview.length} Item)
                      </div>
                      <span className="result-badge">Bebas Diedit</span>
                    </div>
                    <div className="clone-result-body">
                      {renderClonedItems}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ================= INPUT MANUAL (Karyawan Lama) ================= */}
            {!(userMode === "baru" && selectedCloneUnit !== "") && (
              <div className="master-form-section">
                <h4><Edit3 size={18} color="#10b981"/> Detail {modalTab === "kpi" ? "KPI" : "Tabungan"} Baru</h4>
                
                {modalTab === "kpi" ? (
                  <>
                    <div className="rsia-form-group">
                      <label>Indikator KPI</label>
                      <textarea name="indikator_kpi" value={form.indikator_kpi} onChange={handleChange} placeholder="Ketik Indikator KPI secara lengkap..." rows={3} className="master-input-premium" disabled={loading} />
                    </div>
                    <div className="rsia-form-row">
                      <div className="rsia-form-group"><label>Area Kinerja</label><input name="area_kinerja" value={form.area_kinerja} onChange={handleChange} placeholder="Misal: Pelayanan..." className="master-input-premium" disabled={loading} /></div>
                      <div className="rsia-form-group"><label>Parameter</label><input name="parameter" value={form.parameter} onChange={handleChange} placeholder="Parameter penilaian..." className="master-input-premium" disabled={loading} /></div>
                    </div>
                    <div className="rsia-form-group"><label>Sumber</label><input name="sumber" value={form.sumber} onChange={handleChange} placeholder="Misal: Log Book..." className="master-input-premium" disabled={loading} /></div>
                  </>
                ) : (
                  <>
                    <div className="rsia-form-group">
                      <label>Kerja Tabungan Gaji</label>
                      <textarea name="kerja_tabungan_gaji" value={form.kerja_tabungan_gaji} onChange={handleChange} placeholder="Deskripsi pekerjaan..." rows={3} className="master-input-premium" disabled={loading} />
                    </div>
                    <div className="rsia-form-row">
                      <div className="rsia-form-group"><label>Hasil Berwujud</label><input name="hasil_berwujud" value={form.hasil_berwujud} onChange={handleChange} placeholder="Hasil..." className="master-input-premium" disabled={loading} /></div>
                      <div className="rsia-form-group"><label>Parameter</label><input name="parameter" value={form.parameter} onChange={handleChange} placeholder="Parameter..." className="master-input-premium" disabled={loading} /></div>
                    </div>
                    <div className="rsia-form-row">
                      <div className="rsia-form-group"><label>Bukti Nilai</label><input name="bukti_nilai_teks" value={form.bukti_nilai_teks} onChange={handleChange} placeholder="Syarat bukti..." className="master-input-premium" disabled={loading} /></div>
                      <div className="rsia-form-group"><label>Konversi Uang</label><input name="konversi_uang" value={form.konversi_uang} onChange={handleChange} placeholder="Rp..." className="master-input-premium" disabled={loading} /></div>
                    </div>
                  </>
                )}

                <div className="rsia-form-row" style={{ marginTop: "12px" }}>
                  <div className="rsia-form-group"><label>Target Default</label><input name="target" value={form.target} onChange={handleChange} placeholder="Angka Target" className="master-input-premium" disabled={loading} /></div>
                  <div className="rsia-form-group"><label>Bobot (%)</label><input name="bobot" type="number" value={form.bobot} onChange={handleChange} placeholder="Contoh: 15" className="master-input-premium" disabled={loading} /></div>
                </div>
              </div>
            )}
            
          </div>

          <div className="rsia-modal-footer" style={{background: "#ffffff", borderTop: "1px solid #e2e8f0", padding: "16px 24px"}}>
            <button onClick={onClose} disabled={loading} style={{background:"transparent", color:"#64748b", border:"1px solid #cbd5e1", padding: "12px 20px", borderRadius: "10px", fontWeight: "bold", cursor: "pointer"}}>Batal</button>
            <button onClick={handleSubmit} disabled={loading || (userMode === 'baru' && selectedCloneUnit && clonedDataPreview.length === 0)} className="master-btn-save">
              {loading ? "Menyimpan..." : `Simpan ${userMode === 'baru' && selectedCloneUnit ? clonedDataPreview.length + ' Data' : 'Master Data'}`}
            </button>
          </div>
        </div>
      </div>

      {/* 🔥 MODAL INFORMASI (SUKSES/GAGAL) 🔥 */}
      <Modal 
        isOpen={infoModal.isOpen} 
        title={infoModal.title} 
        message={infoModal.message} 
        type={infoModal.type} 
        confirmOnly={infoModal.confirmOnly} 
        confirmLabel={infoModal.confirmLabel} 
        onClose={() => setInfoModal(prev => ({ ...prev, isOpen: false }))} 
        onConfirm={infoModal.onConfirm} 
      />

      {/* 🔥 MODAL LOADING (ANIMASI MUTAR) 🔥 */}
      <Modal 
        isOpen={loading} 
        title="Menyimpan Data" 
        message="Mohon tunggu, sedang memproses ke database..." 
        type="loading" 
        isLoading={true} 
        onClose={null} 
      />
    </>
  );
}